import type { LayoutBlock } from '@web-studio/shared-schemas';
import { REGISTRY_BLOCK } from '@web-studio/shared-schemas';
import { resolveReservationChannelSsrIcon } from '../icons/lucideSsrIconNodes';
import { renderLucideIconHtml, renderSocialIconHtml } from '../icons/renderIconHtml';
import { pickBlockField } from '../lib/localized';
import { pickChrome, type SiteChromeKey } from '../lib/siteChromeCopy';
import {
  resolveExternalHref,
  resolveTelHref,
} from '../blocks/reservation/reservationChannelHref';
import {
  hasContactValue,
  resolveDirectNextChromeKey,
} from '../blocks/reservation/directContactShared';
import { resolveMessengerCtaSocial } from '../blocks/reservation/resolveMessengerSocial';
import { isCtaSocialIconKey } from '../icons/ctaSocialCatalog';
import { escapeHtml } from './escape';

type CtaVoice = 'third-party' | 'messenger' | 'call';

function renderCtaAnchor(options: {
  voice: CtaVoice;
  href: string;
  label: string;
  iconHtml: string;
  openInNewTab: boolean;
}): string {
  const { voice, href, label, iconHtml, openInNewTab } = options;
  if (!label) {
    return '';
  }
  const targetRel = openInNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';
  return `<a class="ws-reservation__cta" data-reservation-cta="${voice}" href="${escapeHtml(href)}"${targetRel}>${iconHtml}<span class="ws-reservation__cta-label">${escapeHtml(label)}</span></a>`;
}

function renderChannelSection(options: {
  block: LayoutBlock;
  locale: string;
  channel: 'third-party' | 'direct';
  componentType: string;
  ctaHtml: string;
  ctaCount?: number;
  nextChromeKey: SiteChromeKey;
  sectionExtraClass?: string;
}): string {
  const {
    block,
    locale,
    channel,
    componentType,
    ctaHtml,
    ctaCount,
    nextChromeKey,
    sectionExtraClass,
  } = options;
  const heading = pickBlockField(block.content, 'heading', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const note = pickBlockField(block.content, 'note', locale);
  const nextStep =
    channel === 'direct'
      ? ''
      : pickBlockField(block.content, 'nextStep', locale) || pickChrome(nextChromeKey, locale);
  const nextHtml = nextStep
    ? `<p class="ws-reservation__next" data-reservation-next="${channel}">${escapeHtml(nextStep)}</p>`
    : '';
  const countAttr =
    typeof ctaCount === 'number' && ctaCount > 0 ? ` data-cta-count="${ctaCount}"` : '';
  const actionInner = ctaHtml
    ? `<div class="ws-reservation__cta-stack"${countAttr}>${ctaHtml}</div>${nextHtml}`
    : nextHtml;
  const extra = sectionExtraClass ? ` ${sectionExtraClass}` : '';

  return `<section class="ws-reservation ws-reservation--channel ws-reservation--${channel}${extra}" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(componentType)}" data-reservation-channel="${channel}" id="reserve">
  <div class="ws-reservation__inner">
    ${heading ? `<h2 class="ws-reservation__title">${escapeHtml(heading)}</h2>` : ''}
    <div class="ws-reservation__copy">
      ${supporting ? `<span class="ws-reservation__subtitle">${escapeHtml(supporting)}</span>` : ''}
      ${note ? `<span class="ws-reservation__note">${escapeHtml(note)}</span>` : ''}
    </div>
    <div class="ws-reservation__action">
      ${actionInner}
    </div>
  </div>
</section>`;
}

function renderDirectContactHtml(
  block: LayoutBlock,
  locale: string,
  componentType: string,
): string {
  const messengerUrl = pickBlockField(block.content, 'messengerUrl', locale);
  const phoneNumber = pickBlockField(block.content, 'phoneNumber', locale);
  const hasMessenger = hasContactValue(messengerUrl);
  const hasCall = hasContactValue(phoneNumber);
  const parts: string[] = [];

  if (hasMessenger) {
    const ctaIcon = pickBlockField(block.content, 'ctaIcon', locale);
    const social = resolveMessengerCtaSocial(ctaIcon, messengerUrl);
    const lucideKey = isCtaSocialIconKey(ctaIcon) ? undefined : ctaIcon;
    const label =
      pickBlockField(block.content, 'ctaLabel', locale) ||
      pickChrome('reservationCtaMessenger', locale);
    const markHtml = social
      ? renderSocialIconHtml(social, {
          size: 32,
          className: 'ws-reservation__cta-mark',
        })
      : (() => {
          const icon = resolveReservationChannelSsrIcon('messenger', lucideKey);
          return renderLucideIconHtml(icon.iconNode, {
            size: 32,
            strokeWidth: 1,
            name: icon.name,
            className: 'ws-reservation__cta-mark',
          });
        })();
    parts.push(
      renderCtaAnchor({
        voice: 'messenger',
        href: resolveExternalHref(messengerUrl),
        label,
        iconHtml: `<span class="ws-reservation__cta-icon">${markHtml}</span>`,
        openInNewTab: true,
      }),
    );
  }

  if (hasCall) {
    const label =
      pickBlockField(block.content, 'callCtaLabel', locale) ||
      pickChrome('reservationCtaCall', locale);
    const callIcon = pickBlockField(block.content, 'callCtaIcon', locale) || 'Phone';
    const icon = resolveReservationChannelSsrIcon('call', callIcon);
    const markHtml = renderLucideIconHtml(icon.iconNode, {
      size: 32,
      strokeWidth: 1,
      name: icon.name,
      className: 'ws-reservation__cta-mark',
    });
    parts.push(
      renderCtaAnchor({
        voice: 'call',
        href: resolveTelHref(phoneNumber),
        label,
        iconHtml: `<span class="ws-reservation__cta-icon">${markHtml}</span>`,
        openInNewTab: false,
      }),
    );
  }

  const extras = [
    hasMessenger ? 'ws-reservation--messenger' : '',
    hasCall ? 'ws-reservation--call' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return renderChannelSection({
    block,
    locale,
    channel: 'direct',
    componentType,
    ctaHtml: parts.join(''),
    ctaCount: parts.length,
    nextChromeKey: resolveDirectNextChromeKey(hasMessenger, hasCall),
    sectionExtraClass: extras || undefined,
  });
}

export function renderReservationThirdPartyBook(block: LayoutBlock, locale: string): string {
  const bookingUrl = pickBlockField(block.content, 'bookingUrl', locale);
  const ctaLabel = pickBlockField(block.content, 'ctaLabel', locale);
  const ctaIcon = pickBlockField(block.content, 'ctaIcon', locale);
  const icon = resolveReservationChannelSsrIcon('third-party', ctaIcon);
  const markHtml = renderLucideIconHtml(icon.iconNode, {
    size: 'md',
    strokeWidth: 1.5,
    name: icon.name,
    className: 'ws-reservation__cta-mark',
  });
  const ctaHtml = renderCtaAnchor({
    voice: 'third-party',
    href: resolveExternalHref(bookingUrl),
    label: ctaLabel,
    iconHtml: `<span class="ws-reservation__cta-icon">${markHtml}</span>`,
    openInNewTab: true,
  });
  return renderChannelSection({
    block,
    locale,
    channel: 'third-party',
    componentType: REGISTRY_BLOCK.presentation.reservationThirdPartyBook,
    ctaHtml,
    ctaCount: ctaHtml ? 1 : 0,
    nextChromeKey: 'reservationNextThirdParty',
  });
}

export function renderReservationMessengerBook(block: LayoutBlock, locale: string): string {
  return renderDirectContactHtml(
    block,
    locale,
    REGISTRY_BLOCK.presentation.reservationMessengerBook,
  );
}

export function renderReservationCallBook(block: LayoutBlock, locale: string): string {
  return renderDirectContactHtml(block, locale, REGISTRY_BLOCK.presentation.reservationCallBook);
}
